import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { Maybe } from '@/shared/libs/helpers';
import { Button } from '@/shared/ui/button';
import type { SortType } from '@/shared/ui/table';

import { TableEmpty } from './TableEmpty';

export type TableRowValue = React.ReactNode | string | number;

export interface TableHeader {
  value: string;
  title?: string | React.ReactNode;
  sortable?: boolean;
  width?: number;
  hide?: boolean;
  key?: boolean;
}

export interface MobileItemConfig<
  T extends Record<string, TableRowValue> = Record<string, TableRowValue>,
> {
  titleSlot?: { key: keyof T };
  subtitleSlot?: { key: keyof T };
  valueSlot?: { key: keyof T };
  actionsSlot?: { key: keyof T };
  checkSlot?: { key: keyof T };
  expandSlot?: { key: keyof T };
  validationErrorSlot?: { key: keyof T };
}

export interface TableControllerProps<
  T extends Record<string, TableRowValue> = Record<string, TableRowValue>,
> {
  data: T[];
  headers: TableHeader[];
  total?: number;
  pageSize?: number;
  onChangePage?: (page: number) => void;
  initPage?: number;
  initSort?: Maybe<SortType>;
  initSortBy?: string;
  onSort?: (sort: SortType, sortBy: string) => void;
  onClickRow?: (event: React.MouseEvent, row: T) => void;
  mobileItemConfig?: MobileItemConfig<T>;
  hasError?: boolean;
  onRefetch?: () => void;
  notResetPage?: boolean;
  expandable?: boolean;
  renderExpandedRow?: (row: T) => React.ReactNode;
  expandedRowClassName?: string;
  emptyState?: {
    title?: string;
    description?: string;
    buttonText?: string;
    filtered?: boolean;
    onClearFilters?: () => void;
    onRefetch?: () => void;
  };
}

export function TableController<T extends Record<string, TableRowValue>>(
  props: Readonly<TableControllerProps<T>>,
) {
  const { t } = useTranslation();
  const {
    data,
    emptyState,
    expandable,
    expandedRowClassName,
    hasError,
    headers,
    initPage = 1,
    initSort,
    initSortBy,
    mobileItemConfig,
    notResetPage = false,
    onChangePage,
    onClickRow,
    onRefetch,
    onSort,
    pageSize = 10,
    renderExpandedRow,
    total,
  } = props;
  const [currentPage, setCurrentPage] = useState(initPage);
  const [currentSort, setCurrentSort] = useState<Maybe<SortType> | undefined>(
    initSort,
  );
  const [currentSortBy, setCurrentSortBy] = useState<string | undefined>(
    initSortBy,
  );

  useEffect(() => {
    setCurrentPage(initPage);
  }, [initPage]);

  useEffect(() => {
    setCurrentSort(initSort);
  }, [initSort]);

  useEffect(() => {
    setCurrentSortBy(initSortBy);
  }, [initSortBy]);

  const visibleHeaders = useMemo(
    () => headers.filter((h) => !h.hide),
    [headers],
  );

  const handleSort = (header: TableHeader) => {
    if (!header.sortable || !onSort) return;

    let newSort: SortType = 'desc';

    if (currentSortBy === header.value) {
      newSort = currentSort === 'asc' ? 'desc' : 'asc';
    }

    setCurrentSort(newSort);
    setCurrentSortBy(header.value);
    onSort(newSort, header.value);

    if (!notResetPage && onChangePage) {
      setCurrentPage(1);
      onChangePage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (onChangePage) {
      onChangePage(page);
    }
  };

  const totalPages = total ? Math.ceil(total / pageSize) : 1;

  if (hasError && onRefetch) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <p className="mb-4 text-destructive">{t('errors.tableLoad')}</p>
        <Button onClick={onRefetch} variant="outline">
          {t('buttons.retry')}
        </Button>
      </div>
    );
  }

  const isEmpty = data.length === 0;

  if (isEmpty) {
    return (
      <div className="w-full overflow-x-auto border rounded-lg bg-card">
        <TableEmpty onRefetch={onRefetch} emptyState={emptyState} />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto border rounded-lg bg-card">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b bg-muted">
              {visibleHeaders.map((header, index) => (
                <th
                  key={index}
                  style={{
                    whiteSpace: header.width ? 'nowrap' : undefined,
                    width: header.width ? `${header.width}px` : undefined,
                  }}
                  className={`px-4 py-3 text-left ${
                    header.sortable ? 'cursor-pointer hover:bg-muted' : ''
                  }`}
                  onClick={() => header.sortable && handleSort(header)}>
                  {header.sortable ? (
                    <button
                      type="button"
                      className="flex items-center gap-1 text-xs font-medium text-foreground uppercase hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSort(header);
                      }}>
                      {header.title}
                      {currentSortBy === header.value ? (
                        <>
                          {currentSort === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-primary" />
                          )}
                        </>
                      ) : (
                        <ArrowUpDown className="w-3 h-3" />
                      )}
                    </button>
                  ) : (
                    <span className="text-xs font-medium text-foreground uppercase">
                      {header.title}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => {
              const keyValue = headers.find((h) => h.key)?.value;
              const rowKey = keyValue ? row[keyValue] : rowIndex;
              const expandedContent =
                !!expandable && !!renderExpandedRow && renderExpandedRow(row);

              return (
                <React.Fragment key={String(rowKey)}>
                  <tr
                    className={`border-b hover:bg-muted transition-colors ${
                      onClickRow ? 'cursor-pointer' : ''
                    }`}
                    onClick={(e) => onClickRow && onClickRow(e, row)}>
                    {visibleHeaders.map((header, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-3 text-sm">
                        {row[header.value]}
                      </td>
                    ))}
                  </tr>
                  {expandedContent && (
                    <tr key={`${String(rowKey)}-expanded`}>
                      <td
                        colSpan={visibleHeaders.length}
                        className={`animate-in slide-in-from-top-2 duration-300 ${
                          expandedRowClassName ?? 'px-4 py-3 bg-muted'
                        }`}>
                        {expandedContent}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile List */}
      {mobileItemConfig && (
        <div className="md:hidden space-y-3">
          {data.map((row, rowIndex) => {
            const keyValue = headers.find((h) => h.key)?.value;
            const rowKey = keyValue ? row[keyValue] : rowIndex;
            const expandedContent =
              !!expandable && !!renderExpandedRow && renderExpandedRow(row);

            return (
              <div key={String(rowKey)}>
                <div className="bg-card border border-border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                  <div
                    className="p-4 space-y-3"
                    onClick={(e) => onClickRow && onClickRow(e, row)}>
                    {mobileItemConfig.checkSlot && (
                      <div className="mb-2">
                        {row[mobileItemConfig.checkSlot.key]}
                      </div>
                    )}
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        {mobileItemConfig.titleSlot && (
                          <div className="font-semibold text-foreground text-base truncate">
                            {row[mobileItemConfig.titleSlot.key]}
                          </div>
                        )}
                        {mobileItemConfig.subtitleSlot && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {row[mobileItemConfig.subtitleSlot.key]}
                          </div>
                        )}
                      </div>
                      {mobileItemConfig.valueSlot && (
                        <div className="text-right flex-shrink-0">
                          {row[mobileItemConfig.valueSlot.key]}
                        </div>
                      )}
                    </div>
                    {mobileItemConfig.validationErrorSlot &&
                      row[mobileItemConfig.validationErrorSlot.key] && (
                        <div className="pt-1">
                          {row[mobileItemConfig.validationErrorSlot.key]}
                        </div>
                      )}
                    {mobileItemConfig.actionsSlot && (
                      <div className="flex justify-end gap-2 pt-2 border-t border-border">
                        {row[mobileItemConfig.actionsSlot.key]}
                      </div>
                    )}
                  </div>
                  {mobileItemConfig.expandSlot && (
                    <div className="border-t border-border flex items-center justify-center">
                      {row[mobileItemConfig.expandSlot.key]}
                    </div>
                  )}
                </div>
                {expandedContent && (
                  <div className="mt-2 animate-in slide-in-from-top-2 duration-300">
                    <div className="bg-muted border border-border rounded-lg p-4 shadow-sm">
                      {expandedContent}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total !== undefined && total > pageSize && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}>
            {t('pagination.previous')}
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-1">
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;

              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (currentPage <= 4) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = currentPage - 3 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}>
                  {pageNum}
                </Button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}>
            {t('pagination.next')}
          </Button>
        </div>
      )}
    </div>
  );
}
